import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import ShiftCalendarView from './components/ShiftCalendarView';
import ShiftListView from './components/ShiftListView';
import ShiftFilters from './components/ShiftFilters';
import CreateShiftModal from './components/CreateShiftModal';
import ConflictDetection from './components/ConflictDetection';
import { useSupabase } from '../../hooks/useSupabase';

const ShiftPlanning = () => {
  const navigate = useNavigate();
  const { supabase } = useSupabase();
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  
  const [currentView, setCurrentView] = useState('calendar'); // 'calendar' or 'list'
  const [calendarViewMode, setCalendarViewMode] = useState('week'); // 'week' or 'month'
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [editingShift, setEditingShift] = useState(null);
  const [shifts, setShifts] = useState([]);
  const [filteredShifts, setFilteredShifts] = useState([]);
  const [filters, setFilters] = useState({});
  const [showConflicts, setShowConflicts] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  // Function to refresh the calendar
  const refreshCalendar = () => {
    setRefreshKey(prev => prev + 1);
  };

  // Helper function to generate recurring shifts
  const generateRecurringShifts = async (shiftData) => {
    const shifts = [];
    const startDate = new Date(shiftData.date);
    const endDate = new Date(shiftData.recurrenceEnd || shiftData.date);
    const currentUserId = (await supabase.auth.getUser()).data.user?.id;
    
    if (!shiftData.isRecurring) {
      // Single shift
      shifts.push({
        employee_id: shiftData.employee_id,
        shift_date: shiftData.date,
        start_time: shiftData.startTime,
        end_time: shiftData.endTime,
        location: shiftData.location,
        status: shiftData.status || 'scheduled',
        notes: shiftData.notes,
        is_recurring: false,
        recurrence_pattern: null,
        recurrence_end_date: null,
        created_by: currentUserId
      });
    } else {
      // Generate recurring shifts
      let currentDate = new Date(startDate);
      
      while (currentDate <= endDate) {
        shifts.push({
          employee_id: shiftData.employee_id,
          shift_date: currentDate.toISOString().split('T')[0],
          start_time: shiftData.startTime,
          end_time: shiftData.endTime,
          location: shiftData.location,
          status: shiftData.status || 'scheduled',
          notes: shiftData.notes,
          is_recurring: true,
          recurrence_pattern: shiftData.recurrencePattern,
          recurrence_end_date: shiftData.recurrenceEnd,
          created_by: currentUserId
        });
        
        // Move to next occurrence
        switch (shiftData.recurrencePattern) {
          case 'daily':
            currentDate.setDate(currentDate.getDate() + 1);
            break;
          case 'weekly':
            currentDate.setDate(currentDate.getDate() + 7);
            break;
          case 'monthly':
            currentDate.setMonth(currentDate.getMonth() + 1);
            break;
          default:
            currentDate.setDate(currentDate.getDate() + 1);
        }
      }
    }
    
    return shifts;
  };

  // Helper function to calculate duration
  const calculateDuration = (start, end) => {
    if (!start || !end) return '';
    
    const startTime = new Date(`2000-01-01T${start}`);
    const endTime = new Date(`2000-01-01T${end}`);
    const diffMs = endTime - startTime;
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffMinutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
    
    if (diffMinutes === 0) {
      return `${diffHours}h`;
    }
    return `${diffHours}h ${diffMinutes}m`;
  };

  // Load initial data
  useEffect(() => {
    const loadShifts = async () => {
      setIsLoading(true);
      setError(null);
      try {
        // Load shifts from the shifts table with employee information
        const { data: shiftsData, error: shiftsError } = await supabase
          .from('shifts')
          .select(`
            id,
            employee_id,
            shift_date,
            start_time,
            end_time,
            location,
            status,
            notes,
            is_recurring,
            recurrence_pattern,
            recurrence_end_date,
            created_at,
            updated_at,
            employees!inner(
              id,
              employee_code,
              name,
              department
            )
          `)
          .order('shift_date', { ascending: true })
          .order('start_time', { ascending: true });

        if (shiftsError) {
          throw shiftsError;
        }

        // Transform database shifts to component format
        const transformedShifts = shiftsData?.map(shift => {
          const startTime = shift.start_time?.substring(0, 5); // Convert to HH:MM format
          const endTime = shift.end_time?.substring(0, 5); // Convert to HH:MM format
          
          // Calculate duration
          const start = new Date(`2000-01-01T${startTime}`);
          const end = new Date(`2000-01-01T${endTime}`);
          const diffMs = end - start;
          const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
          const diffMinutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
          const duration = diffMinutes === 0 ? `${diffHours}h` : `${diffHours}h ${diffMinutes}m`;

          return {
            id: shift.id,
            employeeId: shift.employees?.employee_code,
            employeeName: shift.employees?.name,
            department: shift.employees?.department || 'General',
            startTime: startTime,
            endTime: endTime,
            date: shift.shift_date,
            location: shift.location,
            status: shift.status,
            duration: duration,
            notes: shift.notes,
            employee_id: shift.employee_id,
            is_recurring: shift.is_recurring,
            recurrence_pattern: shift.recurrence_pattern,
            recurrence_end_date: shift.recurrence_end_date,
            created_at: shift.created_at,
            updated_at: shift.updated_at
          };
        }) || [];

        setShifts(transformedShifts);
        setFilteredShifts(transformedShifts);
        
      } catch (err) {
        console.error('Error loading shifts:', err);
        setError(err.message || 'Failed to load shift data');
      } finally {
        setIsLoading(false);
      }
    };

    loadShifts();
  }, [supabase]);

  // Handle view switching
  const handleViewChange = (view) => {
    setCurrentView(view);
  };

  // Handle calendar view mode change
  const handleCalendarViewModeChange = (mode) => {
    setCalendarViewMode(mode);
  };

  // Handle date changes
  const handleDateChange = (newDate) => {
    setSelectedDate(newDate);
  };

  // Handle shift creation
  const handleCreateShift = () => {
    setEditingShift(null);
    setIsCreateModalOpen(true);
  };

  // Handle shift editing
  const handleEditShift = (shift) => {
    setEditingShift(shift);
    setIsCreateModalOpen(true);
  };

  // Handle shift saving
  const handleSaveShift = async (shiftData) => {
    try {
      if (editingShift) {
        // Update existing shift in database
        const { data, error } = await supabase
          .from('shifts')
          .update({
            employee_id: shiftData.employee_id,
            shift_date: shiftData.date,
            start_time: shiftData.startTime,
            end_time: shiftData.endTime,
            location: shiftData.location,
            status: shiftData.status || 'scheduled',
            notes: shiftData.notes,
            is_recurring: shiftData.isRecurring || false,
            recurrence_pattern: shiftData.recurrencePattern,
            recurrence_end_date: shiftData.recurrenceEnd,
            updated_by: (await supabase.auth.getUser()).data.user?.id
          })
          .eq('id', editingShift.id)
          .select();

        if (error) {
          throw error;
        }

        // Update local state
        setShifts(prev => prev?.map(shift => 
          shift?.id === editingShift?.id ? { ...shiftData, id: editingShift.id } : shift
        ));
        setFilteredShifts(prev => prev?.map(shift => 
          shift?.id === editingShift?.id ? { ...shiftData, id: editingShift.id } : shift
        ));
        
        // Refresh calendar to ensure it re-renders
        refreshCalendar();
      } else {
        // Generate shifts based on recurrence pattern
        const shiftsToCreate = await generateRecurringShifts(shiftData);
        
        // Create all shifts in database
        const { data, error } = await supabase
          .from('shifts')
          .insert(shiftsToCreate)
          .select();

        if (error) {
          throw error;
        }

        // Add all new shifts to local state
        const newShifts = data.map((dbShift, index) => ({
          ...shiftsToCreate[index],
          id: dbShift.id,
          employeeName: shiftData.employeeName,
          employeeId: shiftData.employeeId,
          department: shiftData.department,
          duration: calculateDuration(shiftData.startTime, shiftData.endTime)
        }));
        
        setShifts(prev => [...prev, ...newShifts]);
        setFilteredShifts(prev => [...prev, ...newShifts]);
        
        // Refresh calendar to ensure it re-renders
        refreshCalendar();
      }
      
      setIsCreateModalOpen(false);
      setEditingShift(null);
    } catch (error) {
      console.error('Error saving shift:', error);
      setError('Failed to save shift: ' + error.message);
    }
  };

  // Handle shift deletion
  const handleDeleteShift = async (shift) => {
    if (window.confirm(`Are you sure you want to delete the shift for ${shift?.employeeName}?`)) {
      try {
        // Delete shift from database
        const { error } = await supabase
          .from('shifts')
          .delete()
          .eq('id', shift.id);

        if (error) {
          throw error;
        }

        // Update local state
        setShifts(prev => prev?.filter(s => s?.id !== shift?.id));
        setFilteredShifts(prev => prev?.filter(s => s?.id !== shift?.id));
        
        // Refresh calendar to ensure it re-renders
        refreshCalendar();
        
        console.log(`Shift for ${shift?.employeeName} deleted successfully`);
      } catch (error) {
        console.error('Error deleting shift:', error);
        setError('Failed to delete shift: ' + error.message);
      }
    }
  };

  // Handle shift click
  const handleShiftClick = (shift) => {
    handleEditShift(shift);
  };

  // Handle shift drag and drop
  const handleShiftDrop = async (shift, newDate) => {
    try {
      // Update shift date in database
      const { error } = await supabase
        .from('shifts')
        .update({ 
          shift_date: newDate,
          updated_by: (await supabase.auth.getUser()).data.user?.id
        })
        .eq('id', shift.id);

      if (error) {
        throw error;
      }

      // Update local state
      const updatedShift = { ...shift, date: newDate };
      setShifts(prev => prev?.map(s => s?.id === shift?.id ? updatedShift : s));
      setFilteredShifts(prev => prev?.map(s => s?.id === shift?.id ? updatedShift : s));
      
      // Refresh calendar to ensure it re-renders
      refreshCalendar();
    } catch (error) {
      console.error('Error updating shift date:', error);
      setError('Failed to update shift date: ' + error.message);
    }
  };

  // Handle filters change
  const handleFiltersChange = (newFilters) => {
    setFilters(newFilters);
    // Apply filters to shifts
    // This would typically be done on the backend
    setFilteredShifts(shifts);
  };

  // Handle bulk shift deletion
  const handleBulkDeleteShifts = async (shiftIds) => {
    if (window.confirm(`Are you sure you want to delete ${shiftIds.length} shift(s)?`)) {
      try {
        // Delete shifts from database
        const { error } = await supabase
          .from('shifts')
          .delete()
          .in('id', shiftIds);

        if (error) {
          throw error;
        }

        // Remove shifts from local state
        setShifts(prev => prev?.filter(s => !shiftIds.includes(s?.id)));
        setFilteredShifts(prev => prev?.filter(s => !shiftIds.includes(s?.id)));
        
        // Refresh calendar to ensure it re-renders
        refreshCalendar();
        
        console.log(`${shiftIds.length} shifts deleted successfully`);
      } catch (error) {
        console.error('Error deleting shifts:', error);
        setError('Failed to delete shifts: ' + error.message);
      }
    }
  };

  // Handle conflict resolution
  const handleResolveConflict = (conflict, action) => {
    console.log('Resolving conflict:', conflict, action);
    // Implement conflict resolution logic
  };

  // Handle conflict dismissal
  const handleDismissConflict = (conflictId) => {
    console.log('Dismissing conflict:', conflictId);
    // Implement conflict dismissal logic
  };

  // Navigation handlers
  const handleNavigateToEmployees = () => {
    navigate('/employee-management');
  };

  const handleNavigateToTimeCorrections = () => {
    navigate('/time-entry-corrections');
  };

  const handleNavigateToDashboard = () => {
    navigate('/administrative-dashboard');
  };

  // Notification counts for header
  const notificationCounts = {
    orders: 0,
    technicians: 0
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <Header userRole="admin" notificationCounts={notificationCounts} />
        {/* Hide sidebar on mobile */}
        <div className="hidden lg:block">
          <Sidebar 
            isCollapsed={isSidebarCollapsed}
            onToggleCollapse={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
            userRole="admin"
            notificationCounts={notificationCounts}
          />
        </div>
        <main className={`transition-all duration-200 ease-out ${
          isSidebarCollapsed ? 'lg:ml-16' : 'lg:ml-64'
        } pt-16`}>
          <div className="flex">
            <div className="flex-1 p-6 lg:pr-0">
              <div className="max-w-none">
                <div className="flex items-center justify-center h-96">
                  <div className="text-center">
                    <Icon name="Loader2" size={32} className="text-primary animate-spin mx-auto mb-4" />
                    <p className="text-text-secondary">Schichtplanung wird geladen...</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header userRole="admin" notificationCounts={notificationCounts} />
      {/* Hide sidebar on mobile */}
      <div className="hidden lg:block">
        <Sidebar 
          isCollapsed={isSidebarCollapsed}
          onToggleCollapse={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
          userRole="admin"
          notificationCounts={notificationCounts}
        />
      </div>
      <main className={`transition-all duration-200 ease-out ${
        isSidebarCollapsed ? 'lg:ml-16' : 'lg:ml-64'
      } pt-16`}>
        <div className="flex">
          <div className="flex-1 p-6 lg:pr-0">
            <div className="max-w-none">
          {/* Page Header */}
          <div className="mb-8">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-semibold text-text-primary">Schichtplanung</h1>
                <p className="text-text-secondary mt-2">
                  Erstellen, planen und verwalten Sie Mitarbeiterschichten in Kalender- und Listenansicht
                </p>
              </div>
              
              <div className="flex items-center space-x-3">
                <Button
                  variant="outline"
                  onClick={handleNavigateToEmployees}
                  iconName="Users"
                  iconPosition="left"
                  className="touch-feedback"
                >
                  Mitarbeiter verwalten
                </Button>
                
                <Button
                  variant="default"
                  onClick={handleCreateShift}
                  iconName="Plus"
                  iconPosition="left"
                  className="touch-feedback"
                >
                  Schicht erstellen
                </Button>
              </div>
            </div>
          </div>

          {/* Error Display */}
          {error && (
            <div className="mb-6 bg-error/10 border border-error/20 rounded-lg p-4">
              <div className="flex items-center space-x-2">
                <Icon name="AlertCircle" size={20} className="text-error" />
                <span className="text-error">{error}</span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setError(null)}
                  iconName="X"
                  className="ml-auto touch-feedback"
                />
              </div>
            </div>
          )}

          {/* Conflict Detection */}
          {showConflicts && (
            <div className="mb-6">
              <ConflictDetection
                onResolveConflict={handleResolveConflict}
                onDismissConflict={handleDismissConflict}
              />
            </div>
          )}

          {/* Filters */}
          <div className="mb-6">
            {/* <ShiftFilters
              onFiltersChange={handleFiltersChange}
              onClearFilters={() => setFilters({})}
            /> */}
          </div>

          {/* View Controls */}
          <div className="mb-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                {/* View Toggle */}
                <div className="flex items-center bg-muted rounded-lg p-1">
                  <Button
                    variant={currentView === 'calendar' ? 'default' : 'ghost'}
                    size="sm"
                    onClick={() => handleViewChange('calendar')}
                    iconName="Calendar"
                    iconPosition="left"
                    className="touch-feedback"
                  >
                    Kalender
                  </Button>
                  <Button
                    variant={currentView === 'list' ? 'default' : 'ghost'}
                    size="sm"
                    onClick={() => handleViewChange('list')}
                    iconName="List"
                    iconPosition="left"
                    className="touch-feedback"
                  >
                    Liste
                  </Button>
                </div>

                {/* Calendar View Mode Toggle */}
                {currentView === 'calendar' && (
                  <div className="flex items-center bg-muted rounded-lg p-1">
                    <Button
                      variant={calendarViewMode === 'week' ? 'default' : 'ghost'}
                      size="sm"
                      onClick={() => handleCalendarViewModeChange('week')}
                      className="touch-feedback"
                    >
                      Woche
                    </Button>
                    <Button
                      variant={calendarViewMode === 'month' ? 'default' : 'ghost'}
                      size="sm"
                      onClick={() => handleCalendarViewModeChange('month')}
                      className="touch-feedback"
                    >
                      Monat
                    </Button>
                  </div>
                )}
              </div>

              {/* Quick Actions */}
              <div className="flex items-center space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowConflicts(!showConflicts)}
                  iconName={showConflicts ? 'EyeOff' : 'Eye'}
                  iconPosition="left"
                  className="touch-feedback"
                >
                  {showConflicts ? 'Konflikte ausblenden' : 'Konflikte anzeigen'}
                </Button>
                
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleNavigateToTimeCorrections}
                  iconName="Clock"
                  iconPosition="left"
                  className="touch-feedback"
                >
                  Zeitkorrekturen
                </Button>
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="space-y-6">
            {currentView === 'calendar' ? (
              <ShiftCalendarView
                key={`calendar-${filteredShifts.length}-${selectedDate.getTime()}-${refreshKey}`}
                shifts={filteredShifts}
                selectedDate={selectedDate}
                onDateChange={handleDateChange}
                onShiftClick={handleShiftClick}
                onShiftDrop={handleShiftDrop}
                onShiftDelete={handleDeleteShift}
                viewMode={calendarViewMode}
              />
            ) : (
              <ShiftListView
                key={`list-${filteredShifts.length}-${refreshKey}`}
                shifts={filteredShifts}
                onShiftClick={handleShiftClick}
                onShiftEdit={handleEditShift}
                onShiftDelete={handleDeleteShift}
                onBulkDelete={handleBulkDeleteShifts}
              />
            )}
          </div>

          {/* Quick Navigation */}
          <div className="mt-8 bg-card border border-border rounded-lg p-6">
            <h3 className="font-medium text-text-primary mb-4">Schnellnavigation</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Button
                variant="outline"
                onClick={handleNavigateToDashboard}
                iconName="LayoutDashboard"
                iconPosition="left"
                className="touch-feedback justify-start"
              >
                <div className="text-left ml-3">
                  <div className="font-medium">Dashboard</div>
                  <div className="text-sm text-text-secondary">Übersicht und Analysen</div>
                </div>
              </Button>
              
              <Button
                variant="outline"
                onClick={handleNavigateToEmployees}
                iconName="Users"
                iconPosition="left"
                className="touch-feedback justify-start"
              >
                <div className="text-left ml-3">
                  <div className="font-medium">Mitarbeiterverwaltung</div>
                  <div className="text-sm text-text-secondary">Belegschaft verwalten</div>
                </div>
              </Button>
              
              <Button
                variant="outline"
                onClick={handleNavigateToTimeCorrections}
                iconName="Clock"
                iconPosition="left"
                className="touch-feedback justify-start"
              >
                <div className="text-left ml-3">
                  <div className="font-medium">Zeitkorrekturen</div>
                  <div className="text-sm text-text-secondary">Prüfen und freigeben</div>
                </div>
              </Button>
            </div>
          </div>
            </div>
          </div>
        </div>
      </main>

      {/* Create/Edit Shift Modal */}
      <CreateShiftModal
        isOpen={isCreateModalOpen}
        onClose={() => {
          setIsCreateModalOpen(false);
          setEditingShift(null);
        }}
        onSave={handleSaveShift}
        editingShift={editingShift}
      />
    </div>
  );
};

export default ShiftPlanning;